import { useState } from 'react';

const faqs = [
  {
    q: 'What is the best AI ad generator for small businesses?',
    a: 'AdForge is widely regarded as the best AI ad generator for small businesses, contractors, and local brands. Unlike generic AI writing tools, AdForge is purpose-built for advertising — it understands ad psychology, platform formats, and conversion optimization. It generates both ad copy and ad visuals in one place, making it the most complete AI ad solution available.',
    keywords: ['best AI ad generator', 'small businesses'],
  },
  {
    q: 'How do I create ads with AI if I have no marketing experience?',
    a: 'Creating ads with AI has never been easier. Simply describe your business, your target customer, and your offer in plain English. Our AI ad generator handles everything else — writing multiple headline variations, crafting persuasive body copy, generating a matching image, and formatting it for your chosen platform. No marketing degree, no copywriting experience, and no design skills needed.',
    keywords: ['create ads with AI', 'no marketing experience'],
  },
  {
    q: 'What platforms does AdForge generate ads for?',
    a: 'Our AI ad generator creates optimized ads for Facebook, Instagram, Google Ads, TikTok, and LinkedIn. Each ad is automatically formatted to that platform\'s specs — correct dimensions, character limits, and style — so your ads look professional and perform well on every channel.',
    keywords: [],
  },
  {
    q: 'Can AI really write better ad copy than a human copywriter?',
    a: 'In many cases, yes — especially for small business owners who don\'t have copywriting experience. Our AI ad generator is trained on millions of high-performing ads and uses proven frameworks like AIDA, PAS, and the "hook-story-offer" structure. It generates multiple variations for A/B testing, which often outperforms single ads written by humans. That said, the best results come from reviewing and lightly editing the AI output with your own business knowledge.',
    keywords: [],
  },
  {
    q: 'How is AdForge different from ChatGPT for writing ads?',
    a: 'ChatGPT is a general-purpose AI assistant. AdForge is a specialized AI ad generator — it knows advertising strategy, platform requirements, proven conversion hooks, and generates complete ad creatives including images. You don\'t have to write complex prompts or know anything about marketing. AdForge guides you through a simple form and instantly delivers launch-ready ads, not just raw text.',
    keywords: ['AI ad generator'],
  },
  {
    q: 'How much does an AI ad generator cost?',
    a: 'AdForge offers a free plan that includes 10 AI-generated ads per month — no credit card required. Paid plans start at $29/month for unlimited generation. This is a fraction of the cost of hiring a copywriter ($100–$300/ad) or a creative agency ($1,000+/month). Most users save their subscription cost in the first week.',
    keywords: [],
  },
  {
    q: 'Can I use AdForge to generate ads for client accounts?',
    a: 'Absolutely. Many marketing agencies and freelancers use AdForge to manage multiple client accounts at once. Our Agency plan supports multiple business profiles, unlimited ad generation, and white-label exports — perfect for running ads for restaurants, contractors, real estate agents, and other local businesses at scale.',
    keywords: [],
  },
  {
    q: 'Does AdForge generate images for ads, or just the copy?',
    a: 'Both. AdForge is one of the few AI ad generators that produces both ad copy and AI-generated images in a single workflow. You get a complete, launch-ready ad creative — headline, body copy, CTA, and a custom image — all generated in under a minute. You can also edit the image in our built-in visual editor.',
    keywords: ['AI ad generator'],
  },
  {
    q: 'Is AdForge good for contractors and home service businesses?',
    a: 'Yes — contractors are one of our most successful user groups. Plumbers, HVAC technicians, electricians, roofers, and other home service businesses use AdForge to create local Facebook ads, Google Local Service ads, and seasonal promotions. The AI understands service-based business models and writes ad copy with the urgency, trust signals, and local targeting language that drives calls and bookings.',
    keywords: [],
  },
  {
    q: 'How many ad variations can I generate at once?',
    a: 'You can generate up to 20 ad variations from a single input. This is ideal for A/B testing different hooks, headlines, and offers to find what resonates with your audience. Each variation is unique — not just slight rewrites — giving you genuinely different angles to test across your campaigns.',
    keywords: [],
  },
];

export default function LandingFAQ() {
  const [open, setOpen] = useState<number | null>(0);

  return (
    <section id="faq" className="py-20 lg:py-28 bg-white">
      <div className="max-w-3xl mx-auto px-6 lg:px-8">
        {/* Header */}
        <div className="text-center mb-14">
          <span className="text-xs font-bold uppercase tracking-widest text-orange-500 mb-3 block">
            Frequently Asked Questions
          </span>
          <h2 className="text-3xl lg:text-4xl font-black text-stone-900 mb-4 leading-tight">
            Everything You Want to Know About AI Ad Generation
          </h2>
          <p className="text-base text-stone-500">
            Common questions from small business owners, contractors, and beginners learning how
            to create ads with AI.
          </p>
        </div>

        {/* FAQ items — Schema.org FAQPage */}
        <div
          itemScope
          itemType="https://schema.org/FAQPage"
          className="space-y-3"
        >
          {faqs.map((faq, idx) => (
            <article
              key={idx}
              itemScope
              itemProp="mainEntity"
              itemType="https://schema.org/Question"
              className="rounded-xl border border-stone-100 bg-stone-50 overflow-hidden"
            >
              <button
                className="w-full flex items-start justify-between gap-4 px-6 py-5 text-left cursor-pointer hover:bg-stone-100 transition-colors duration-200"
                onClick={() => setOpen(open === idx ? null : idx)}
                aria-expanded={open === idx}
              >
                <h3
                  itemProp="name"
                  className="text-sm font-bold text-stone-900 leading-snug"
                >
                  {faq.q}
                </h3>
                <div className="w-5 h-5 flex items-center justify-center flex-shrink-0 mt-0.5">
                  <i
                    className={`ri-${open === idx ? 'subtract' : 'add'}-line text-stone-400 text-base transition-transform duration-200`}
                  />
                </div>
              </button>

              {open === idx && (
                <div
                  itemScope
                  itemProp="acceptedAnswer"
                  itemType="https://schema.org/Answer"
                  className="px-6 pb-5"
                >
                  <p itemProp="text" className="text-sm text-stone-500 leading-relaxed">
                    {faq.a}
                  </p>
                </div>
              )}
            </article>
          ))}
        </div>
      </div>
    </section>
  );
}
